import type {BaseItemLayout, CompRef, GridItemLayout, Margins, SingleLayoutData} from '@wix/document-services-types'
import {COMP_TYPES, DATA_TYPES} from '../../../constants/constants'
import type {ExtensionAPI} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../../structure'
import type {VariantsExtensionAPI} from '../../variants/variants'
import type {DataModelAPI} from '../../dataModel/dataModel'
import type {ManifestApi} from '../../builderComponents/manifest'
import type {CalculateParams, ConverterArguments, ConvertWidthToPx} from './types'
import {CANVAS_HEIGHT, CANVAS_WIDTH} from './constants'
import {isColumnsCalculationInProgress, isRowsCalculationInProgress} from './rowsColumnsCalculationProgress'
import {ReportableError} from '@wix/document-manager-utils'
import type {LoggerExtAPI} from '../../logger'
import type {SlotsExtensionAPI} from '../../slots/slots'
import type {ScopesExtensionAPI} from '../../scopes'

export const getLayout = (extensionAPI: ExtensionAPI, compRef: CompRef): SingleLayoutData => {
    const {variants} = extensionAPI as VariantsExtensionAPI
    return variants.getItemConsideringVariantsForPointer(compRef, DATA_TYPES.layout)
}

const sectionTypes = [COMP_TYPES.RESPONSIVE_SECTION, COMP_TYPES.HEADER_SECTION, COMP_TYPES.FOOTER_SECTION]
export const isSection = (extensionAPI: ExtensionAPI, compRef: CompRef): boolean => {
    const {components} = extensionAPI as StructureExtensionAPI
    return sectionTypes.includes(components.getComponentType(compRef))
}

const pageTypes = [COMP_TYPES.PAGE_TYPE]
export const isPage = (extensionAPI: ExtensionAPI, compRef: CompRef): boolean => {
    const {components} = extensionAPI as StructureExtensionAPI
    return pageTypes.includes(components.getComponentType(compRef))
}

export const clampMinMax = (value: number, min?: number, max?: number): number => {
    let res = value
    if (typeof min === 'number') res = Math.max(min, res)
    if (typeof max === 'number') res = Math.min(max, res)
    return res
}

export const getStaleMeasurements = (dataModel: DataModelAPI, compRef: CompRef) => {
    return dataModel.components.getItem(compRef, DATA_TYPES.measurements)
}

export const getManifestInitialSize = (manifest: ManifestApi, compRef: CompRef) => {
    return manifest.layout.getInitialSize(compRef)
}

export const getValue = (object: any) => ('value' in object ? object.value : undefined)

export const getMin = (object: any) => ('min' in object ? object.min : undefined)

export const getMax = (object: any) => ('max' in object ? object.max : undefined)

export function getParentWidth(converterArguments: ConverterArguments) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    calculate.parent.width(converterArguments)
    const parentRef = pointers.structure.getParent(compRef) as CompRef
    const parentId = parentRef?.id
    if (parentId && !isColumnsCalculationInProgress(parentId)) {
        calculate.parent.columns(converterArguments)
    }

    return getColumnsWidthFromGridArea(converterArguments) ?? get.width(parentRef) ?? CANVAS_WIDTH
}

export function getParentHeight(converterArguments: ConverterArguments) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    calculate.parent.height(converterArguments)
    const parentRef = pointers.structure.getParent(compRef) as CompRef
    const parentId = parentRef?.id
    if (parentId && !isRowsCalculationInProgress(parentId)) {
        calculate.parent.rows(converterArguments)
    }

    return getRowsHeightFromGridArea(converterArguments) ?? get.height(parentRef) ?? CANVAS_HEIGHT
}

function getColumnsWidthFromGridArea(converterArguments: ConverterArguments) {
    const gridArea = (converterArguments.layoutData?.itemLayout as GridItemLayout)?.gridArea
    if (!gridArea) return undefined

    return getColumnsWidth(converterArguments, gridArea.columnStart - 1, gridArea.columnEnd - 1)
}

export function getLeftColumnsWidth(converterArguments: ConverterArguments, columnGapPx?: number): number {
    const {gridArea} = converterArguments.layoutData.itemLayout as GridItemLayout
    if (!gridArea) return 0

    const columnsWidth = getColumnsWidth(converterArguments, 0, gridArea.columnStart - 1)
    const gap = columnGapPx ?? 0
    const gapsBeforeItem = Math.max(0, gridArea.columnStart - 1)
    return columnsWidth + gapsBeforeItem * gap
}

function getColumnsWidth(converterArguments: ConverterArguments, columnStart: number, columnEnd: number) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    const parentRef = pointers.structure.getParent(compRef) as CompRef
    const parentId = parentRef?.id
    if (parentId && !isColumnsCalculationInProgress(parentId)) {
        calculate.parent.columns(converterArguments)
    }
    const columns = get.columns(parentRef)
    if (!columns) return 0

    let columnsWidth = 0
    for (let i = columnStart; i < columnEnd; i++) {
        columnsWidth += columns[i]
    }
    return columnsWidth
}

function getRowsHeightFromGridArea(converterArguments: ConverterArguments) {
    const gridArea = (converterArguments.layoutData?.itemLayout as GridItemLayout)?.gridArea
    if (!gridArea) return undefined

    return getRowsHeight(converterArguments, gridArea.rowStart - 1, gridArea.rowEnd - 1)
}

export function getTopRowsHeight(converterArguments: ConverterArguments): number {
    const {gridArea} = converterArguments.layoutData.itemLayout as GridItemLayout
    if (!gridArea) return 0

    return getRowsHeight(converterArguments, 0, gridArea.rowStart - 1)
}

function getRowsHeight(converterArguments: ConverterArguments, rowStart: number, rowEnd: number) {
    const {measurementsMapAPI, calculate, pointers, compRef} = converterArguments
    const {get} = measurementsMapAPI

    const parentRef = pointers.structure.getParent(compRef) as CompRef
    const parentId = parentRef?.id
    if (parentId && !isRowsCalculationInProgress(parentId)) {
        calculate.parent.rows(converterArguments)
    }
    const rows = get.rows(parentRef)
    if (!rows) return 0

    let rowsHeight = 0
    for (let i = rowStart; i < rowEnd; i++) {
        rowsHeight += rows[i]
    }
    return rowsHeight
}

function getMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins: Margins | undefined,
    side: keyof Margins
): number {
    return margins?.[side]?.type
        ? convertWidthToPx[margins[side]!.type]({
              ...converterArguments,
              value: getValue(margins[side])
          })
        : 0
}

// in CSS top and bottom margins are calculated based on width
export function getTopMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'top')
}
export function getBottomMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'bottom')
}
export function getLeftMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'left')
}
export function getRightMarginInPx(
    converterArguments: ConverterArguments,
    convertWidthToPx: ConvertWidthToPx,
    margins?: Margins
) {
    return getMarginInPx(converterArguments, convertWidthToPx, margins, 'right')
}

export function calculateHeightBasedOnChildren(
    {measurementsMapAPI, calculate, pointers, extensionAPI}: CalculateParams,
    convertWidthToPx: ConvertWidthToPx,
    children: CompRef[]
): number {
    const {get} = measurementsMapAPI
    let lowestChildBottomLine = 0
    let bottomMargin = 0
    children?.forEach(childRef => {
        const childLayoutData = getLayout(extensionAPI, childRef)
        const type = childLayoutData.componentLayout?.height?.type
        if (!type || type === 'percentage') {
            return
        }

        const calculateParams = {
            measurementsMapAPI,
            calculate,
            compRef: childRef,
            layoutData: childLayoutData,
            pointers,
            extensionAPI
        }

        calculate.height(calculateParams)

        const {margins} = childLayoutData.itemLayout as BaseItemLayout<string>
        const topMargin = getTopMarginInPx(calculateParams, convertWidthToPx, margins)
        const height = get.height(childRef)

        if (topMargin + height > lowestChildBottomLine) {
            lowestChildBottomLine = topMargin + height
            bottomMargin = getBottomMarginInPx(calculateParams, convertWidthToPx, margins)
        }
    })
    return lowestChildBottomLine + bottomMargin
}

export function reportEndlessLoopError({extensionAPI, compRef, layoutData}: CalculateParams) {
    const {logger} = extensionAPI as LoggerExtAPI
    const reportableError = new ReportableError({
        errorType: 'measurementsEndlessLoop',
        message: 'Got an endless loop during fallback measurements calculation for mobile algo',
        extras: {
            compRef,
            layoutData
        }
    })
    logger.captureError(reportableError)
}

export function reportCalculationError({extensionAPI, compRef, layoutData}: CalculateParams) {
    const {logger} = extensionAPI as LoggerExtAPI
    const reportableError = new ReportableError({
        errorType: 'measurementsCalculation',
        message: 'An error during measurements calculation',
        extras: {
            compRef,
            layoutData
        }
    })
    logger.captureError(reportableError)
}

export function getChildrenWithoutPlugins({pointers, compRef, extensionAPI}: CalculateParams): CompRef[] {
    const {slots, scopes} = extensionAPI as SlotsExtensionAPI & ScopesExtensionAPI
    const childrenRefs = pointers.structure.getChildren(compRef) as CompRef[]

    return childrenRefs.filter(child => {
        let childRef = child
        if (scopes.hasDefinedScopes(childRef)) {
            childRef = scopes.getTemplateCompPointer(childRef)
        }
        return !slots.isChildOfSlottedComp(childRef.id) || slots.is.unifiedContainerPlugin(childRef)
    })
}
